// framework/common/schemas/collections/schedules.js
import { z } from "zod";

// ---------------------------------------------------------------------------
// Dispatch modes — controls which instance(s) execute a fired schedule.
//
//  'one'      (default) — SchedulerServer calls targetService via ServiceMesh.
//                         ServiceMesh picks one instance by load/topology.
//                         Exactly-once by construction. No lock needed.
//
//  'all'      — SchedulerServer publishes to DCS scope:'global'.
//               Every running instance of the subscribing app handles it.
//               Intended for cache invalidation, local state refresh, etc.
//
//  'tag'      — ServiceMesh call filtered to instances carrying a specific tag.
//               Useful for routing to a dedicated worker pool.
//               Requires dispatch.tag to be set.
//
//  'instance' — ServiceMesh call to a specific instanceId.
//               Useful for tasks pinned to a specific pod (e.g. a leader node).
//               Requires dispatch.instanceId to be set.
// ---------------------------------------------------------------------------
const DispatchSchema = z.object({
    mode: z.enum(['one', 'all', 'tag', 'instance']).default('one'),
    tag:        z.string().optional().nullable(),   // required when mode='tag'
    instanceId: z.string().optional().nullable(),   // required when mode='instance'
}).default({ mode: 'one' });

export default z.object({
    _id:         z.string().optional(),
    name:        z.string().min(1),
    description: z.string().optional(),
    schedule:    z.string(),

    actionType:  z.enum(['event', 'rpc', 'workflow', 'aiMessage']),
    actionConfig: z.object({}).passthrough(),
    // actionConfig shapes per actionType:
    //
    //  rpc:       { targetService: string, rpcMethod: string, params?: object }
    //  event:     { eventName: string, eventData?: object }
    //  workflow:  { targetService: string, workflowName: string, workflowArgs?: object }
    //  aiMessage: { agentName: string, message: string, contextId?: string, streaming?: boolean }
    //
    // targetService is required for rpc/workflow when dispatch.mode !== 'all'.
    // For mode:'all' it is used as a filter so only matching apps handle the DCS event.

    dispatch: DispatchSchema,

    context:   z.object({}).passthrough().optional(),
    category:  z.enum(['system', 'user', 'campaign', 'automation']).default('user'),
    tags:      z.array(z.string()).optional().default([]),
    enabled:   z.boolean().default(true),
    nextRun:   z.date().optional().nullable(),
    lastRun:   z.date().optional().nullable(),
    timeZone:  z.string().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),

    // Set by SchedulerServerConnector — identifies the originating app.
    sourceService:  z.string().optional(),
    // Subscription context stamped at document root for index queries.
    subscriptionId: z.string().optional(),
});